import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Play,
  Pause,
  Save,
  AlertCircle,
  Users,
  Loader2,
  Brain,
  X,
  ArrowUpRight
} from 'lucide-react';
import { SpeakerLabels, SpeakerSegment } from '../types';
import { startSpeakerInferenceSession } from '../services/agentService';
import { SpeakerAudioService } from '../services/speakerAudioService';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { getSpeakerColorValue } from '../utils/speakerColors';

interface EditSpeakersModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  speakers: number[];
  speakerSegments: Map<number, SpeakerSegment[]>;
  currentLabels: SpeakerLabels;
  onSave: (labels: SpeakerLabels) => Promise<void>;
  transcriptionChecksum?: string;
  isYouTubeMode?: boolean;
  embedded?: boolean;
}

const EditSpeakersModal: React.FC<EditSpeakersModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  speakers,
  speakerSegments,
  currentLabels,
  onSave,
  transcriptionChecksum,
  embedded = false
}) => {
  const { token } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [labels, setLabels] = useState<SpeakerLabels>(() => {
    const initial: SpeakerLabels = {};
    speakers.forEach(id => {
      initial[id] = currentLabels?.[id] || `Speaker ${id}`;
    });
    return initial;
  });
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [isInferring, setIsInferring] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inferenceAgentSessionId, setInferenceAgentSessionId] = useState<string | null>(null);
  const audioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  // Reset labels when modal opens with new currentLabels
  useEffect(() => {
    if (isOpen) {
      const initial: SpeakerLabels = {};
      speakers.forEach(id => {
        initial[id] = currentLabels?.[id] || `Speaker ${id}`;
      });
      setLabels(initial);
      setError('');
      setInferenceAgentSessionId(null);
    }
  }, [isOpen, currentLabels, speakers]);

  // Cleanup audio when modal closes
  useEffect(() => {
    if (!isOpen) {
      audioRefs.current.forEach((audio) => {
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
        audio.pause();
      });
      audioRefs.current.clear();
      setPlayingAudio(null);
    }
  }, [isOpen]);

  const inferSpeakerNames = async () => {
    if (!sessionId || speakers.length === 0 || isInferring) {
      return;
    }

    try {
      setIsInferring(true);
      setError('');

      const result = await startSpeakerInferenceSession({
        sessionId,
        enableWebSearch: true,
      }, token || undefined);
      setInferenceAgentSessionId(result.agentSessionId);

    } catch (error) {
      console.error('Speaker name inference failed:', error);
      setError('Failed to start the speaker inference agent. You can still label speakers manually.');
    } finally {
      setIsInferring(false);
    }
  };

  const handleLabelChange = (speakerId: number, label: string) => {
    setLabels(prev => ({
      ...prev,
      [speakerId]: label
    }));
  };

  const playAudioSample = async (speakerId: number) => {
    try {
      setError('');

      // Stop any currently playing audio
      if (playingAudio !== null) {
        const currentAudio = audioRefs.current.get(playingAudio);
        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }
      }

      if (!transcriptionChecksum) {
        setError('Audio not available');
        return;
      }

      let audio = audioRefs.current.get(speakerId);
      if (!audio) {
        try {
          const audioUrl = await SpeakerAudioService.fetchSpeakerSampleUrl(
            transcriptionChecksum,
            speakerId,
            token || undefined
          );
          audio = new Audio(audioUrl);
          audioRefs.current.set(speakerId, audio);
        } catch (fetchError) {
          console.error('Failed to fetch audio:', fetchError);
          setError(`Failed to load audio sample for Speaker ${speakerId}.`);
          return;
        }

        audio.addEventListener('ended', () => {
          setPlayingAudio(null);
        });

        audio.addEventListener('error', () => {
          setError(`Failed to play audio sample for Speaker ${speakerId}`);
          setPlayingAudio(null);
        });
      }

      audio.currentTime = 0;
      setPlayingAudio(speakerId);
      await audio.play();

    } catch (error) {
      console.error('Audio playback error:', error);
      setError(`Failed to play audio sample for Speaker ${speakerId}`);
      setPlayingAudio(null);
    }
  };

  const stopAudioSample = (speakerId: number) => {
    const audio = audioRefs.current.get(speakerId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingAudio(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');

      // Fill in default labels for speakers without custom names
      const completedLabels: SpeakerLabels = {};
      speakers.forEach(speakerId => {
        completedLabels[speakerId] = labels[speakerId] || `Speaker ${speakerId}`;
      });

      await onSave(completedLabels);
      if (!embedded) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save speaker labels:', error);
      setError('Failed to save speaker labels. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getSpeakerSegmentCount = (speakerId: number): number => {
    return speakerSegments.get(speakerId)?.length || 0;
  };

  const getSpeakerDuration = (speakerId: number): number => {
    const segments = speakerSegments.get(speakerId) || [];
    return segments.reduce((total, segment) => total + (segment.end - segment.start), 0);
  };

  if (!isOpen) return null;

  return (
    <div className={embedded ? '' : 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} ${embedded ? `w-full rounded-2xl border p-0 shadow-none ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}` : 'mx-4 max-w-2xl rounded-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto'} overflow-y-auto`}
      >
        {!embedded && (
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-800">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Edit Speaker Names
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Update speaker labels for this session
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {speakers.length === 0 ? (
          <div className={`${embedded ? 'p-6' : 'py-8'} text-center`}>
            <AlertCircle size={48} className="text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
              No Speakers Detected
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              This session doesn't have multiple speakers or speaker diarization data.
            </p>
          </div>
        ) : (
          <div className={`space-y-4 ${embedded ? 'p-6' : ''}`}>
            {/* AI Inference Button */}
            {transcriptionChecksum && (
              <div className="flex items-center justify-end space-x-2">
                {isInferring && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400"
                  >
                    <Loader2 size={16} className="animate-spin" />
                    <span>AI is analyzing...</span>
                  </motion.div>
                )}

                {!isInferring && (
                  <button
                    onClick={inferSpeakerNames}
                    className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                    title="Start the speaker inference agent"
                  >
                    <Brain size={16} />
                    <span>Start Naming Agent</span>
                  </button>
                )}
              </div>
            )}

            {inferenceAgentSessionId && !isInferring && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-2">
                    <Brain size={16} className="text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Speaker inference agent started
                    </span>
                  </div>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-agent', {
                      detail: { agentSessionId: inferenceAgentSessionId },
                    }))}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Open agent
                    <ArrowUpRight size={12} />
                  </button>
                </div>
                <p className={`mt-2 text-xs ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                  The agent will infer speaker labels through the ReScript CLI and save them back onto this session. Open the agent tab if you want to supervise or interject.
                </p>
              </motion.div>
            )}

            {/* Speaker List */}
            <div className="space-y-3">
              {speakers.map((speakerId) => (
                <div
                  key={speakerId}
                  className={`flex items-center space-x-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: getSpeakerColorValue(speakerId, isDarkMode) }}
                      />
                      <div className={`p-2 rounded-lg shadow-sm ${isDarkMode ? 'bg-gray-600' : 'bg-white'}`}>
                        <User size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Speaker {speakerId}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {getSpeakerSegmentCount(speakerId)} segments • {Math.round(getSpeakerDuration(speakerId))}s
                        </span>
                      </div>

                      <input
                        type="text"
                        value={labels[speakerId] || ''}
                        onChange={(e) => handleLabelChange(speakerId, e.target.value)}
                        placeholder={`Enter name for Speaker ${speakerId}...`}
                        className={`w-full px-3 py-2 text-sm border rounded-lg transition-all duration-200 ${
                          isDarkMode
                            ? 'border-gray-600 bg-gray-800 text-gray-100 focus:border-primary-500'
                            : 'border-gray-200 bg-white text-gray-900 focus:border-primary-500'
                        } focus:ring-1 focus:ring-primary-200`}
                      />
                    </div>
                  </div>

                  {transcriptionChecksum && (
                    <button
                      onClick={() =>
                        playingAudio === speakerId
                          ? stopAudioSample(speakerId)
                          : playAudioSample(speakerId)
                      }
                      disabled={playingAudio !== null && playingAudio !== speakerId}
                      className={`
                        flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-200
                        ${playingAudio === speakerId
                          ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700'
                          : 'bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-700'
                        }
                        disabled:opacity-50 disabled:cursor-not-allowed
                      `}
                    >
                      {playingAudio === speakerId ? (
                        <>
                          <Pause size={16} />
                          <span className="text-sm">Stop</span>
                        </>
                      ) : (
                        <>
                          <Play size={16} />
                          <span className="text-sm">Play</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Error Display */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-center space-x-2"
                >
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              {!embedded && (
                <button
                  onClick={onClose}
                  disabled={isSaving}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary flex items-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EditSpeakersModal;
